/**
 * @ipc/validation · src/schemas/logs.ts — фильтры чтения логов
 * (раздел 10 "Логирование и наблюдаемость", раздел 13 API-граница).
 *
 * `auditLogFiltersSchema` — вход `GET /api/v1/logs/audit` (AuditLog: действия
 * админов/RBAC). Имена query-параметров стабильны для UI; маппинг на колонки
 * таблицы AuditLog (раздел 02, schema/access.ts) делает контроллер:
 *   userId → adminId (bigint админа-актора, точное совпадение),
 *   action → action  (напр. "refund.create", точное совпадение),
 *   path   → entityType (тип затронутой сущности: Payment|Refund|PaymentLink…),
 *   from/to → createdAt (ISO-границы, включительно),
 *   search  → LIKE-поиск по action/entityType/entityId (ilike, OR),
 *   sortBy/sortOrder → сортировка (createdAt|action), дефолт createdAt desc.
 * Пагинация {page,limit} — общий paginationSchema, как у соседних роутов логов.
 */
import { z } from 'zod/v4';
import {
  paginationSchema,
  searchSchema,
  sortOrderSchema,
  paymentProviderSchema,
} from '../common/primitives.ts';

export const auditLogFiltersSchema = paginationSchema.extend({
  userId: z.coerce.number().int().positive().optional(),
  action: z.string().min(1).optional(),
  path: z.string().min(1).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  search: searchSchema,
  sortBy: z.enum(['createdAt', 'action']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type AuditLogFiltersDto = z.infer<typeof auditLogFiltersSchema>;

/**
 * `webhookLogFiltersSchema` — вход `GET /api/v1/logs/webhooks` (WebhookLog:
 * сырые тела входящих webhook, раздел 07/10). Зеркало схемы контроллера
 * (`apps/backend/src/controllers/logs.controller.ts`) — invoiceId/uuid ищутся
 * подстрокой в rawBody, from/to — границы receivedAt, search — доп. LIKE-поиск
 * по rawBody, provider — точное совпадение (enum WebhookLogProvider, раздел 02;
 * набор значений совпадает с каноническим `paymentProviderSchema`).
 * Ответ переведён на постраничную выдачу с meta.pagination (эталон /logs/audit) —
 * приводим наравне с остальными списками, отдельного одностраничного контракта
 * больше нет (было расхождение с остальными /logs-роутами).
 */
export const webhookLogFiltersSchema = paginationSchema.extend({
  invoiceId: z.string().optional(),
  uuid: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  search: searchSchema,
  provider: paymentProviderSchema.optional(),
  sortBy: z.enum(['receivedAt']).default('receivedAt'),
  sortOrder: sortOrderSchema,
});
export type WebhookLogFiltersDto = z.infer<typeof webhookLogFiltersSchema>;
